import axios, { AxiosInstance, AxiosResponse } from 'axios';
import {
  User,
  Message,
  LoginCredentials,
  RegisterCredentials,
  ApiResponse,
  PaginatedResponse,
  MessageInput,
  DeepSeekRequest,
  DeepSeekResponse,
} from '../types';

class ApiService {
  private api: AxiosInstance;
  private baseURL: string;

  constructor() {
    this.baseURL = import.meta.env.REACT_APP_API_URL || 'http://localhost:3001';
    this.api = axios.create({
      baseURL: this.baseURL,
      timeout: 10000,
      headers: {
        'Content-Type': 'application/json',
      },
    });

    // Request interceptor to add auth token
    this.api.interceptors.request.use(
      (config) => {
        const token = localStorage.getItem('authToken');
        if (token) {
          config.headers.Authorization = `Bearer ${token}`;
        }
        return config;
      },
      (error) => Promise.reject(error)
    );

    // Response interceptor for error handling
    this.api.interceptors.response.use(
      (response) => response,
      (error) => {
        if (error.response?.status === 401) {
          // Token expired or invalid
          localStorage.removeItem('authToken');
          localStorage.removeItem('user');
          window.location.href = '/login';
        }
        return Promise.reject(error);
      }
    );
  }

  // Authentication endpoints
  async login(credentials: LoginCredentials): Promise<ApiResponse<{ user: User; token: string }>> {
    try {
      const response: AxiosResponse<ApiResponse<{ user: User; token: string }>> = await this.api.post(
        '/auth/login',
        credentials
      );
      return response.data;
    } catch (error: any) {
      throw this.handleError(error);
    }
  }

  async register(credentials: RegisterCredentials): Promise<ApiResponse<{ user: User; token: string }>> {
    try {
      const response: AxiosResponse<ApiResponse<{ user: User; token: string }>> = await this.api.post(
        '/auth/register',
        credentials
      );
      return response.data;
    } catch (error: any) {
      throw this.handleError(error);
    }
  }

  async logout(): Promise<ApiResponse> {
    try {
      const response: AxiosResponse<ApiResponse> = await this.api.post('/auth/logout');
      localStorage.removeItem('authToken');
      localStorage.removeItem('user');
      return response.data;
    } catch (error: any) {
      throw this.handleError(error);
    }
  }

  async verifyToken(): Promise<ApiResponse<User>> {
    try {
      const response: AxiosResponse<ApiResponse<User>> = await this.api.get('/auth/verify');
      return response.data;
    } catch (error: any) {
      throw this.handleError(error);
    }
  }

  // User endpoints
  async getProfile(): Promise<ApiResponse<User>> {
    try {
      const response: AxiosResponse<ApiResponse<User>> = await this.api.get('/users/profile');
      return response.data;
    } catch (error: any) {
      throw this.handleError(error);
    }
  }

  async updateProfile(userData: Partial<User>): Promise<ApiResponse<User>> {
    try {
      const response: AxiosResponse<ApiResponse<User>> = await this.api.put('/users/profile', userData);
      return response.data;
    } catch (error: any) {
      throw this.handleError(error);
    }
  }

  async changePassword(oldPassword: string, newPassword: string): Promise<ApiResponse> {
    try {
      const response: AxiosResponse<ApiResponse> = await this.api.put('/users/password', {
        oldPassword,
        newPassword,
      });
      return response.data;
    } catch (error: any) {
      throw this.handleError(error);
    }
  }

  // Message endpoints
  async getMessages(roomId: string, page = 1, limit = 50): Promise<ApiResponse<PaginatedResponse<Message>>> {
    try {
      const response: AxiosResponse<ApiResponse<PaginatedResponse<Message>>> = await this.api.get(
        `/messages/${roomId}`,
        {
          params: { page, limit },
        }
      );
      return response.data;
    } catch (error: any) {
      throw this.handleError(error);
    }
  }

  async sendMessage(messageData: MessageInput): Promise<ApiResponse<Message>> {
    try {
      const response: AxiosResponse<ApiResponse<Message>> = await this.api.post('/messages', messageData);
      return response.data;
    } catch (error: any) {
      throw this.handleError(error);
    }
  }

  async deleteMessage(messageId: string): Promise<ApiResponse> {
    try {
      const response: AxiosResponse<ApiResponse> = await this.api.delete(`/messages/${messageId}`);
      return response.data;
    } catch (error: any) {
      throw this.handleError(error);
    }
  }

  // AI Integration endpoints
  async sendToAI(request: DeepSeekRequest): Promise<ApiResponse<DeepSeekResponse>> {
    try {
      const response: AxiosResponse<ApiResponse<DeepSeekResponse>> = await this.api.post('/deepseek/chat', request);
      return response.data;
    } catch (error: any) {
      throw this.handleError(error);
    }
  }

  // Health check
  async healthCheck(): Promise<ApiResponse> {
    try {
      const response: AxiosResponse<ApiResponse> = await this.api.get('/health');
      return response.data;
    } catch (error: any) {
      throw this.handleError(error);
    }
  }

  // Error handler
  private handleError(error: any): Error {
    if (error.response) {
      // Server responded with an error
      const message = error.response.data?.message || error.response.data?.error || 'Erro do servidor';
      return new Error(message);
    } else if (error.request) {
      // Network error
      return new Error('Erro de rede. Por favor, verifique sua conexão.');
    } else {
      // Other error
      return new Error(error.message || 'Ocorreu um erro inesperado');
    }
  }

  // Utility methods
  setBaseURL(url: string): void {
    this.baseURL = url;
    this.api.defaults.baseURL = url;
  }

  getBaseURL(): string {
    return this.baseURL;
  }
}

const apiService = new ApiService();
export default apiService; 